import React from "react";
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Typography,
  Avatar,
  Chip,
  Skeleton,
  Fade,
  Badge,
  Divider,
} from "@mui/material";
import { AccessTime, ArrowForwardIos } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { FormattedMessage } from "react-intl";
import TimeAgo from "react-timeago";
import { formatter } from "../../../helpers";

/**
 * List-optimized notification component designed for use within containers
 * Provides a clean, minimal design that works well in list contexts
 */
export const ListNotificationBase = ({
  notification,
  prevNotification,
  getNotificationTypeHeadline,
  onClick,
  isProcessing,
  children,
  // Notification-specific props
  notificationIcon,
  actionLabel,
  actionIcon,
  actionColor = "primary",
  showTypeBadge = false,
  typeBadgeIcon,
  isLast = false,
  ...listItemProps
}) => {
  const { contributor, createdAt, wasSeen, course } = notification;

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          position: "relative",
          opacity: isProcessing ? 0.7 : 1,
          transform: isProcessing ? "scale(0.98)" : "scale(1)",
          transition: "all 0.2s ease-in-out",
          ...listItemProps.sx,
        }}
      >
        <ListItemButton
          onClick={onClick}
          disabled={isProcessing}
          sx={{
            px: 2,
            py: 1.5,
            alignItems: "flex-start",
            "&:hover": {
              backgroundColor: "action.hover",
            },
            "&.Mui-disabled": {
              color: "text.primary",
            },
          }}
          aria-label={
            actionLabel
              ? `${actionLabel} from ${contributor?.username}`
              : `Notification from ${contributor?.username}`
          }
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, width: "100%" }}>
            {/* Avatar with unread indicator */}
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              {!wasSeen && (
                <UnreadIndicator
                  size="small"
                  color="primary"
                  sx={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    zIndex: 1,
                    width: 10,
                    height: 10,
                    minHeight: 10,
                  }}
                />
              )}

              {showTypeBadge && typeBadgeIcon ? (
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    <TypeBadge size="small">{typeBadgeIcon}</TypeBadge>
                  }
                >
                  <Avatar
                    src={contributor?.profilePicture}
                    alt={contributor?.username}
                    sx={{
                      width: 40,
                      height: 40,
                      border: !wasSeen ? "2px solid" : "2px solid transparent",
                      borderColor: !wasSeen ? "primary.main" : "transparent",
                      transition: "border-color 0.2s ease-in-out",
                    }}
                  />
                </Badge>
              ) : (
                <Avatar
                  src={contributor?.profilePicture}
                  alt={contributor?.username}
                  sx={{
                    width: 40,
                    height: 40,
                    border: !wasSeen ? "2px solid" : "2px solid transparent",
                    borderColor: !wasSeen ? "primary.main" : "transparent",
                    transition: "border-color 0.2s ease-in-out",
                  }}
                />
              )}
            </Box>

            {/* Content area */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: wasSeen ? 600 : 700,
                  mb: 1,
                  lineHeight: 1.2,
                  color: wasSeen ? "text.secondary" : "text.primary",
                  fontSize: "1rem",
                  letterSpacing: "-0.01em",
                }}
              >
                <FormattedMessage
                  id="notification.notificationText"
                  values={{
                    headline: notification.notificationType,
                    username: contributor?.username,
                  }}
                />
              </Typography>

              {/* Course information */}
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    fontSize: "0.875rem",
                    mb: 0.25,
                  }}
                >
                  {course?.title}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    opacity: 0.8,
                  }}
                >
                  {course?.university}
                </Typography>
              </Box>

              {/* Timestamp and action indicator */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <AccessTime sx={{ fontSize: 14, color: "text.secondary", opacity: 0.7 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ 
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      opacity: 0.8,
                    }}
                  >
                    <TimeAgo date={Number(createdAt)} formatter={formatter} />
                  </Typography>
                </Box>

                {actionLabel && (
                  <Chip
                    icon={actionIcon}
                    label={actionLabel}
                    size="small"
                    color={actionColor}
                    variant="outlined"
                    sx={{
                      fontSize: "0.75rem",
                      height: 22,
                      fontWeight: 500,
                      "& .MuiChip-icon": {
                        fontSize: 12,
                      },
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Action arrow */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                opacity: 0.4,
              }}
            >
              <ArrowForwardIos sx={{ fontSize: 14, color: "text.secondary" }} />
            </Box>
          </Box>

          {/* Loading overlay */}
          {isProcessing && (
            <Fade in={isProcessing}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1,
                }}
              >
                <Skeleton variant="circular" width={20} height={20} />
              </Box>
            </Fade>
          )}
        </ListItemButton>
      </ListItem>

      {/* Divider between notifications */}
      {!isLast && (
        <Divider
          variant="inset"
          component="li"
          sx={{
            ml: 6, // Align with avatar + gap
            mr: 2,
          }}
        />
      )}
    </>
  );
};

// Styled components
const UnreadIndicator = styled(Chip)(({ theme }) => ({
  "&.MuiChip-root": {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    minHeight: 10,
    height: 10,
    width: 10,
    padding: 0,
    "& .MuiChip-label": {
      display: "none",
    },
  },
}));

const TypeBadge = styled(Chip)(({ theme }) => ({
  "&.MuiChip-root": {
    backgroundColor: theme.palette.secondary.main,
    color: "white",
    minHeight: 16,
    height: 16,
    width: 16,
    padding: 0,
    "& .MuiChip-icon": {
      fontSize: 10,
      margin: 0,
    },
    "& .MuiChip-label": {
      display: "none",
    },
  },
}));

// Helper function to check if previous notification was same type
const wasPrevNotifSameType = (
  notificationType,
  prevNotificationType,
  getNotificationTypeHeadline
) => {
  if (!prevNotificationType || !getNotificationTypeHeadline) return false;

  const currentHeadline = getNotificationTypeHeadline(notificationType);
  const prevHeadline = getNotificationTypeHeadline(prevNotificationType);

  return currentHeadline?.props?.id === prevHeadline?.props?.id;
};
